import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { PERMISSIONS } from '../../../constants/permissions';
import { DashboardService } from '../../../services/dashboard.service';
import { AttendanceSummary, AttendanceKpis, AttendanceKpi, DailyCompositionPoint, ArrivalHistogram, WeekdayLatePoint, EmployeeLate, EmployeeOt, DepartmentAttendance, TodayLog, PunchLogRow } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import Beeswarm from '../charts/Beeswarm';
import Columns from '../charts/Columns';
import StatTile from '../charts/StatTile';
import Sparkline from '../charts/Sparkline';
import StackedColumns from '../charts/StackedColumns';
import HBars from '../charts/HBars';
import HStack100 from '../charts/HStack100';
import TableView from '../charts/TableView';
import useKpiTargets from '../useKpiTargets';
import OrgUnitPicker from '../../common/OrgUnitPicker';

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW_MANAGER;
// HR sees institution-wide attendance/leave anyway; the summary endpoints only need DASHBOARD_VIEW
// server-side. Department heads hold the *_VIEW_ALL key but never DASHBOARD_VIEW, so stay out.
const HR_ACCESS = [[PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.ATTENDANCE_VIEW_ALL]];

const NO_SHIFT_MSG = 'No shift data for this range — configure shifts to see late/overtime.';

const EmptyNote: React.FC<{ msg?: string }> = ({ msg = NO_SHIFT_MSG }) => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{msg}</Typography>
);

const WindowCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{children}</Typography>
);

function useAttendance(institutionId: string) {
    const [data, setData] = useState<AttendanceSummary | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceSummary(institutionId).then(d => { if (live) setData(d); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    return data;
}

const DayPulseWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const d = useAttendance(institutionId);
    if (!d) return <CircularProgress size={20} />;
    // DA-15: people whose shift has not started yet are not absent.
    const notStarted = Math.min(d.notStartedYetToday ?? 0, d.absentToday);
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label="Present" value={d.presentToday} sub={`of ${d.totalUsers}`} tone="good" />
                <StatTile label="On leave" value={d.onLeaveToday} />
                <StatTile label="Absent" value={d.absentToday - notStarted} tone={d.absentToday - notStarted > 0 ? 'warning' : 'default'}
                    sub={notStarted > 0 ? `${notStarted} not started yet` : undefined} />
            </Stack>
            <Beeswarm minutes={d.arrivalMinutesToday} />
        </Box>
    );
};

const TrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const d = useAttendance(institutionId);
    if (!d) return <CircularProgress size={20} />;
    return <Columns data={d.trend.map(p => ({ label: p.date.slice(5), value: p.present }))} />;
};

// attendance.onTimeRate targets are framed as the complement of lateShare (100 − late%), matching
// the label on the KPI-targets settings screen rather than the raw late-share metric.
function statValueForTarget(key: keyof AttendanceKpis, k: AttendanceKpi): number {
    return key === 'lateShare' ? 100 - k.value : k.value;
}

const KPI_META: {
    key: keyof AttendanceKpis; label: string; targetKey?: string;
    // The late-share tile shows a raw late % but meters against the on-time complement, so its
    // caption must name the metric ("On-time target: 85%") or it reads as a wildly missed target.
    targetLabel?: (target: number) => string;
}[] = [
    { key: 'attendanceRate', label: 'Attendance rate', targetKey: 'attendance.presentRate', targetLabel: t => `Target: ${t}%` },
    { key: 'lateShare', label: 'Late share', targetKey: 'attendance.onTimeRate', targetLabel: t => `On-time target: ${t}%` },
    { key: 'overtimeHours', label: 'Overtime' },
    { key: 'avgDayWorked', label: 'Avg day worked' },
];

function fmtKpiValue(k: AttendanceKpi): string {
    return `${Number.isInteger(k.value) ? k.value : k.value.toFixed(1)}${k.unit}`;
}

// delta = value - previous window, so previous ~ 0 means there is nothing to compare against
// (a "+100%" that just repeats the value is noise, not a trend).
function hasComparison(k: AttendanceKpi): boolean {
    return Math.abs(k.value - k.delta) >= 0.05;
}

function fmtKpiDelta(k: AttendanceKpi): string {
    if (!hasComparison(k)) return '—';
    return `${k.delta >= 0 ? '+' : ''}${k.delta.toFixed(1)}${k.unit}`;
}

// tone follows improvement direction, not raw value: a metric where lower is better
// (late share, overtime) is "good" when its delta is negative.
function kpiTone(k: AttendanceKpi): 'default' | 'good' | 'warning' {
    if (k.delta === 0 || !hasComparison(k)) return 'default';
    const improving = k.higherIsBetter ? k.delta > 0 : k.delta < 0;
    return improving ? 'good' : 'warning';
}

const KpisWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<AttendanceKpis | null>(null);
    const targets = useKpiTargets(institutionId);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceKpis(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.attendanceRate.sparkline.length === 0) return <EmptyNote />;
    return (
        <Box>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {KPI_META.map(({ key, label, targetKey, targetLabel }) => {
                const k = d[key];
                const target = targetKey ? targets[targetKey] : undefined;
                const value = statValueForTarget(key, k);
                const meter = target != null && target > 0 ? Math.max(0, Math.min(100, (value / target) * 100)) : undefined;
                return (
                    <Box key={key} sx={{ minWidth: 150 }}>
                        <StatTile label={label} value={fmtKpiValue(k)} sub={fmtKpiDelta(k)} tone={kpiTone(k)} target={target} meter={meter} targetLabel={target != null && targetLabel ? targetLabel(target) : undefined} />
                        <Box sx={{ px: 1.5 }}><Sparkline values={k.sparkline} /></Box>
                    </Box>
                );
            })}
        </Stack>
        {/* These are rolling-window figures, not today's: the tiles cover the last 30 days of shift
            records, and today only counts people who have punched so far. Without the caption a
            morning reading looks like a missed target. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Last 30 days, shift-based · today counts people who have punched so far · change is vs the previous 30 days
        </Typography>
        </Box>
    );
};

const CompositionWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<DailyCompositionPoint[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceComposition(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
            <StackedColumns
                data={d.map(p => ({ label: p.date.slice(5), segments: [p.onTime, p.late, p.onLeave, p.absent] }))}
                seriesLabels={['On-time', 'Late', 'Leave', 'Absent']}
                colorIndices={[0, 1, 3, 2]}
                tickEvery={7}
            />
            <WindowCaption>Last 60 days, shift-based</WindowCaption>
        </Box>
    );
};

function minuteToSlot(min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60 < 30 ? '00' : '30';
    return `${String(h).padStart(2, '0')}:${m}`;
}

const ArrivalsWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<ArrivalHistogram | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceArrivals(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.buckets.length === 0) return <EmptyNote msg="No arrivals recorded for this range." />;
    const markerSlot = d.shiftStartMinute != null ? minuteToSlot(d.shiftStartMinute) : null;
    const markerIndex = markerSlot != null ? d.buckets.findIndex(b => b.slot === markerSlot) : -1;
    const marker = markerIndex >= 0 ? { index: markerIndex, label: markerSlot as string } : undefined;
    return (
        <Box>
            <Columns data={d.buckets.map(b => ({ label: b.slot, value: b.count }))} marker={marker} />
            <WindowCaption>Last 30 days · every punch-in, local time</WindowCaption>
        </Box>
    );
};

const WeekdayWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<WeekdayLatePoint[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceWeekday(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0 || d.every(p => p.worked === 0)) return <EmptyNote />;
    return (
        <Box>
            <Columns
                data={d.map(p => ({ label: p.weekday, value: p.worked > 0 ? Math.round((p.late / p.worked) * 100) : 0 }))}
                colorIndex={1}
                yFmt={(n) => `${n}%`}
            />
            <WindowCaption>Last 60 days</WindowCaption>
        </Box>
    );
};

const TopLateWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<EmployeeLate[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTopLate(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
        <HBars
            data={d.map(e => ({
                label: e.name,
                value: e.lateDays,
                tooltip: [
                    { label: 'Late days', value: String(e.lateDays) },
                    { label: 'Late minutes', value: String(e.lateMinutes) },
                ],
            }))}
            colorIndex={1}
        />
        <WindowCaption>Last 30 days · late days</WindowCaption>
        </Box>
    );
};

const TopOtWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<EmployeeOt[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTopOt(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
        <HBars
            data={d.map(e => {
                const hours = Math.round((e.overtimeMinutes / 60) * 10) / 10;
                return {
                    label: e.name,
                    value: hours,
                    tooltip: [
                        { label: 'Overtime', value: `${hours} h` },
                        { label: 'Days worked', value: String(e.daysWorked) },
                    ],
                };
            })}
            colorIndex={4}
        />
        <WindowCaption>Last 30 days · overtime hours</WindowCaption>
        </Box>
    );
};

const DepartmentWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<DepartmentAttendance[] | null>(null);
    const [orgUnitId, setOrgUnitId] = useState<string | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceDepartment(institutionId, 60, orgUnitId ?? undefined).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId, orgUnitId]);
    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <OrgUnitPicker
                    institutionId={institutionId}
                    value={orgUnitId}
                    onChange={option => setOrgUnitId(option?.id ?? null)}
                    label="Org Unit"
                    placeholder="All departments, floors, sections, lines..."
                    sx={{ flex: '1 1 240px', maxWidth: 360 }}
                    onError={() => {}}
                    data-testid="attendance-department-org-unit-filter"
                />
            </Box>
            {!d ? (
                <CircularProgress size={20} />
            ) : d.length === 0 ? (
                <EmptyNote />
            ) : (
                <>
                    <HStack100
                        rows={d.map(x => ({ label: x.department, segments: [x.onTime, x.late, x.absent] }))}
                        seriesLabels={['On-time', 'Late', 'Absent']}
                        colorIndices={[0, 1, 2]}
                    />
                    <WindowCaption>Last 60 days, shift-based</WindowCaption>
                </>
            )}
        </Box>
    );
};

function minToHHmm(min: number | null): string {
    if (min == null) return '—';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TodayLogWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<TodayLog | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTodayLog(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.rows.length === 0) return <EmptyNote msg="No punches recorded today." />;
    return (
        <Box>
            <TableView
                columns={['Name', 'In', 'Out', 'Late']}
                rows={d.rows.map((r: PunchLogRow) => [
                    r.name,
                    minToHHmm(r.inMinute),
                    // A lone check-in stores out == in; show "—" (still in) rather than a fake 0-minute day.
                    r.outMinute != null && r.outMinute === r.inMinute ? '—' : minToHHmm(r.outMinute),
                    r.late ? `${r.lateMinutes}m late` : 'On-time',
                ])}
            />
            {d.absentNotShown > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {d.absentNotShown} absent not shown
                </Typography>
            )}
        </Box>
    );
};

export const attendanceWidgets: WidgetDef[] = [
    { id: 'attendance.dayPulse', module: 'attendance', title: 'Day Pulse — who is in today', subtitle: 'Today\'s headcount at work and when people arrived', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: DayPulseWidget },
    { id: 'attendance.trend', module: 'attendance', title: 'Attendance — last 14 days', subtitle: 'People present each day, last 14 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: TrendWidget },
    { id: 'attendance.kpis', module: 'attendance', title: 'Attendance KPIs', subtitle: 'Attendance rate, lateness and overtime vs the previous 30 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: KpisWidget },
    { id: 'attendance.composition', module: 'attendance', title: 'Daily composition', subtitle: 'Daily split of on-time, late, on-leave and absent staff', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: CompositionWidget },
    { id: 'attendance.arrivals', module: 'attendance', title: 'Arrivals by half-hour', subtitle: 'When people clock in, in half-hour slots, last 30 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: ArrivalsWidget },
    { id: 'attendance.weekday', module: 'attendance', title: 'Late share by weekday', subtitle: 'Which weekdays run late most often, last 60 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: WeekdayWidget },
    { id: 'attendance.topLate', module: 'attendance', title: 'Most late days', subtitle: 'Employees with the most late days, last 30 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: TopLateWidget },
    { id: 'attendance.topOt', module: 'attendance', title: 'Most overtime', subtitle: 'Employees with the most overtime hours, last 30 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: TopOtWidget },
    { id: 'attendance.department', module: 'attendance', title: 'Attendance by department', subtitle: 'Attendance rate for each department, last 60 days', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: DepartmentWidget },
    { id: 'attendance.todayLog', module: 'attendance', title: 'Punch log · today', subtitle: 'Every punch recorded today, newest first', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: TodayLogWidget },
];
